import { db } from "./dbClient";

// ─── ComplianceCalendarPage.jsx ───────────────────────────────────────────────
// Monthly compliance deadline calendar — SAR, CTR, EDD, KYC Refresh, Training.
// Manual entries: Add / Edit / Delete. Today button. CSV export.

import { useState, useEffect, useCallback } from "react";
import { useTheme } from "./theme";
import { useAuditLog } from "./useAuditLog";
import { useTranslation } from "react-i18next";

// ─── Helpers ──────────────────────────────────────────────────────────────────
function isoDate(d) {
  if (!d) return null;
  let dt;

  // If it's already in YYYY-MM-DD format, return it as-is
  if (typeof d === 'string' && d.match(/^\d{4}-\d{2}-\d{2}$/)) {
    return d;
  }

  // Try to parse as ISO string or timestamp
  if (typeof d === 'string') {
    // For strings like "2026-08-31", parse as UTC
    if (d.includes('-') && !d.includes('T')) {
      return d; // Already in correct format
    }
    dt = new Date(d);
  } else if (typeof d === 'number') {
    dt = new Date(d);
  } else {
    dt = new Date(d);
  }

  if (isNaN(dt.getTime())) return null;
  return dt.toISOString().slice(0, 10);
}

function todayStr() {
  // Use local date instead of UTC to match user's timezone
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function addDays(dateStr, n) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function daysFromToday(dateStr) {
  const t = new Date(todayStr());
  const d = new Date(dateStr);
  return Math.ceil((d - t) / 86400000);
}

function monthDays(year, month) {
  const days = [];
  const d = new Date(year, month, 1);
  while (d.getMonth() === month) {
    days.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return days;
}

function downloadCSV(rows, filename) {
  const headers = ["Date", "Type", "Description", "Status", "Days Until Due"];
  const lines = [headers.join(",")];
  rows.forEach(r => {
    lines.push([
      `"${r.date}"`,
      `"${r.type}"`,
      `"${(r.label || "").replace(/"/g, "'")}"`,
      `"${r.status || ""}"`,
      `"${daysFromToday(r.date)}"`,
    ].join(","));
  });
  const blob = new Blob([lines.join("\n")], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  a.click(); URL.revokeObjectURL(url);
}

const SYSTEM_TYPES = ["SAR", "CTR", "EDD", "KYC Refresh", "Training"];
const MANUAL_TYPES = ["SAR", "CTR", "EDD", "KYC Refresh", "Training", "Board Meeting", "Policy Review", "Regulatory Exam", "Internal Audit", "Other"];

const BLANK_ENTRY = { date: "", type: "Other", label: "", notes: "", status: "pending" };

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ComplianceCalendarPage({ company, currentUser }) {
  const C = useTheme();
  const { log } = useAuditLog(company, currentUser);
  const { t } = useTranslation();

  const MONTH_NAMES = [t('calendar.january'), t('calendar.february'), t('calendar.march'), t('calendar.april'), t('calendar.may'), t('calendar.june'), t('calendar.july'), t('calendar.august'), t('calendar.september'), t('calendar.october'), t('calendar.november'), t('calendar.december')];
  const DOW = [t('calendar.sunday'), t('calendar.monday'), t('calendar.tuesday'), t('calendar.wednesday'), t('calendar.thursday'), t('calendar.friday'), t('calendar.saturday')];

  const TYPE_META = {
    SAR:             { label: t('calendar.sarFiling'),      color: "#FF4444" },
    CTR:             { label: t('calendar.ctrFiling'),       color: "#FF8C00" },
    EDD:             { label: t('calendar.eddReview'),       color: "#A78BFA" },
    "KYC Refresh":   { label: t('calendar.kycRefresh'),     color: "#00D4FF" },
    Training:        { label: t('calendar.trainingDue'),     color: "#00E676" },
    "Board Meeting": { label: t('calendar.boardMeeting'),   color: "#F59E0B" },
    "Policy Review": { label: t('calendar.policyReview'),   color: "#EC4899" },
    "Regulatory Exam":{ label: t('calendar.regExam'),       color: "#6366F1" },
    "Internal Audit":{ label: t('calendar.internalAudit'),  color: "#14B8A6" },
    Other:           { label: t('calendar.other'),            color: "#9CA3AF" },
  };

  const now = new Date();
  const [viewYear, setViewYear]       = useState(now.getFullYear());
  const [viewMonth, setViewMonth]     = useState(now.getMonth());
  const [deadlines, setDeadlines]     = useState([]);
  const [manualEntries, setManualEntries] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [viewDays, setViewDays]       = useState(30); // 30, 60, 90, or 120 days

  // Modal state
  const [showModal, setShowModal]     = useState(false);
  const [editingEntry, setEditingEntry] = useState(null); // null = new, object = editing
  const [draft, setDraft]             = useState({ ...BLANK_ENTRY });
  const [saving, setSaving]           = useState(false);

  // ─── Fetch system deadlines ─────────────────────────────────────────────────
  const fetchDeadlines = useCallback(async () => {
    if (!company?.id) return;
    setLoading(true); setError("");
    const items = [];
    try {
      // SAR deadlines
      try {
        const { data: sars } = await db.from("sars").select("id, filing_deadline, status, subject_name").eq("company_id", company.id);
        (sars || []).filter(r => r.status !== "filed").forEach(r => { const d = isoDate(r.filing_deadline); if (d) items.push({ id: `sar-${r.id}`, date: d, type: "SAR", label: `SAR: ${r.subject_name || r.id}`, status: r.status, system: true }); });
      } catch (e) { console.warn("[Calendar] SAR fetch failed:", e.message); }

      // CTR deadlines
      try {
        const { data: ctrs } = await db.from("ctrs").select("id, filing_deadline, status, entity_name").eq("company_id", company.id);
        (ctrs || []).filter(r => r.status !== "filed").forEach(r => { const d = isoDate(r.filing_deadline); if (d) items.push({ id: `ctr-${r.id}`, date: d, type: "CTR", label: `CTR: ${r.entity_name || r.id}`, status: r.status, system: true }); });
      } catch (e) { console.warn("[Calendar] CTR fetch failed:", e.message); }

      // EDD deadlines
      try {
        const { data: edds } = await db.from("edd_reviews").select("id, review_date, status, customer_name").eq("company_id", company.id);
        (edds || []).filter(r => r.status !== "complete").forEach(r => { const d = isoDate(r.review_date); if (d) items.push({ id: `edd-${r.id}`, date: d, type: "EDD", label: `EDD: ${r.customer_name || r.id}`, status: r.status, system: true }); });
      } catch (e) { console.warn("[Calendar] EDD fetch failed:", e.message); }

      // KYC refresh deadlines
      try {
        const { data: kycs } = await db.from("kyc_submissions").select("id, status").eq("company_id", company.id).eq("status", "approved");
        (kycs || []).forEach(r => {
          // Use current date + 1 year as placeholder if next_review_date doesn't exist
          const nextReviewDate = new Date();
          nextReviewDate.setFullYear(nextReviewDate.getFullYear() + 1);
          const d = isoDate(nextReviewDate.toISOString());
          if (d) items.push({ id: `kyc-${r.id}`, date: d, type: "KYC Refresh", label: `KYC: ${r.id}`, status: r.status, system: true });
        });
      } catch (e) { console.warn("[Calendar] KYC fetch failed:", e.message); }

      // Training deadlines
      try {
        const { data: trains } = await db.from("training_assignments").select("id, due_date, status").eq("company_id", company.id);
        (trains || []).filter(r => r.status !== "completed").forEach(r => {
          const d = isoDate(r.due_date);
          if (d) items.push({ id: `train-${r.id}`, date: d, type: "Training", label: `Training: ${r.id}`, status: r.status, system: true });
        });
      } catch (e) { console.warn("[Calendar] Training fetch failed:", e.message); }

      setDeadlines(items);
    } catch (err) {
      setError("Failed to load deadlines: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [company?.id]);

  // ─── Fetch manual entries ───────────────────────────────────────────────────
  const fetchManual = useCallback(async () => {
    if (!company?.id) return;
    try {
      const { data, error } = await db.from("calendar_entries")
        .select("*")
        .eq("company_id", company.id)
        .order("date", true)
        .then(({ data, error }) => {
          if (error) throw error;
          return { data: (data || []).sort((a, b) => (a.date || "").localeCompare(b.date || "")), error };
        })
        .catch(err => ({ data: [], error: err }));

      if (error) {
        console.error('[Calendar] Load manual entries error:', error.message);
        return;
      }

      setManualEntries((data || []).map(r => ({
        id: `manual-${r.id}`,
        dbId: r.id,
        date: isoDate(r.date) || r.date, // Normalize date to YYYY-MM-DD format
        type: r.type || "Other",
        label: r.label || "",
        notes: r.notes || "",
        status: r.status || "pending",
        system: false,
      })));
    } catch (err) {
      console.error('[Calendar] Load manual entries failed:', err.message);
    }
  }, [company?.id]);

  useEffect(() => { fetchDeadlines(); fetchManual(); }, [fetchDeadlines, fetchManual]);

  // ─── Calendar helpers ────────────────────────────────────────────────────────
  function goToday() {
    const n = new Date();
    setViewYear(n.getFullYear());
    setViewMonth(n.getMonth());
    setSelectedDate(todayStr());
  }
  function prevMonth() {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
  }
  function nextMonth() {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
  }

  const allItems = [...deadlines, ...manualEntries];
  const days = monthDays(viewYear, viewMonth);
  const firstDow = new Date(viewYear, viewMonth, 1).getDay();
  const deadlineMap = {};
  allItems.forEach(item => {
    if (!deadlineMap[item.date]) deadlineMap[item.date] = [];
    deadlineMap[item.date].push(item);
  });

  // Debug: Log calendar data
  console.log('[Calendar] View:', { year: viewYear, month: viewMonth, daysInMonth: days.length });
  console.log('[Calendar] All items:', allItems.length, allItems.slice(0, 5).map(i => ({ date: i.date, label: i.label })));
  console.log('[Calendar] Items by date:', Object.keys(deadlineMap).sort());

  const tStr = todayStr();
  const inViewDays = addDays(tStr, viewDays);
  const upcoming = allItems.filter(d => d.date >= tStr && d.date <= inViewDays).sort((a, b) => a.date.localeCompare(b.date));
  const overdue  = allItems.filter(d => d.date < tStr).sort((a, b) => b.date.localeCompare(a.date));

  function urgencyBg(dateStr) {
    const diff = daysFromToday(dateStr);
    if (diff < 0)  return `${C.danger}20`;
    if (diff <= 3) return `${C.warn}20`;
    return "transparent";
  }
  function urgencyText(dateStr) {
    const diff = daysFromToday(dateStr);
    if (diff < 0)  return C.danger;
    if (diff <= 3) return C.warn;
    return C.textMid;
  }

  // ─── Add / Edit ─────────────────────────────────────────────────────────────
  function openAdd(prefillDate) {
    setEditingEntry(null);
    setDraft({ ...BLANK_ENTRY, date: prefillDate || tStr });
    setShowModal(true);
  }

  function openEdit(item) {
    setEditingEntry(item);
    setDraft({ date: item.date, type: item.type, label: item.label, notes: item.notes || "", status: item.status || "pending" });
    setShowModal(true);
  }

  async function handleSave() {
    if (!draft.date || !draft.label.trim()) return;
    setSaving(true);

    // Ensure date is in YYYY-MM-DD format
    const dateStr = typeof draft.date === 'string' ? draft.date.trim() : '';
    if (!dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      console.error('[Calendar] Invalid date format:', draft.date);
      alert('Please enter a valid date (YYYY-MM-DD format)');
      setSaving(false);
      return;
    }

    const payload = { company_id: company.id, date: dateStr, type: draft.type, label: draft.label.trim(), notes: draft.notes, status: draft.status };
    console.log('[Calendar] Saving entry with payload:', payload);

    try {
      if (editingEntry) {
        // Update
        const { error: err } = await db.from("calendar_entries").update(payload).eq("id", editingEntry.dbId);
        if (err) {
          console.error('[Calendar] Update error:', err.message);
          alert('Update failed: ' + err.message);
          setSaving(false);
          return;
        }
        await fetchManual();
        log({ action: "calendar_entry_updated", module: "compliance_calendar", entityName: draft.label });
      } else {
        // Insert
        const { error: err } = await db.from("calendar_entries").insert({ ...payload, created_by: currentUser?.email });
        if (err) {
          console.error('[Calendar] Insert error:', err.message);
          alert('Insert failed: ' + err.message);
          setSaving(false);
          return;
        }
        await fetchManual();
        log({ action: "calendar_entry_added", module: "compliance_calendar", entityName: draft.label });
      }
    } catch (e) {
      console.error('[Calendar] Save failed:', e.message);
    } finally {
      setSaving(false);
      setShowModal(false);
      setEditingEntry(null);
    }
  }

  // ─── Delete ──────────────────────────────────────────────────────────────────
  async function handleDelete(item) {
    if (!item.dbId) {
      console.error('[Calendar] No dbId for item:', item);
      return;
    }
    if (!window.confirm(`Delete "${item.label}"?`)) return;
    try {
      console.log('[Calendar] Deleting item with dbId:', item.dbId);
      const { error: err } = await db.from("calendar_entries").delete().eq("id", item.dbId);
      if (err) {
        console.error('[Calendar] Delete error:', err);
        alert('Failed to delete entry: ' + err.message);
        return;
      }
      console.log('[Calendar] Delete successful, reloading manual entries');
      await fetchManual();
      // If the deleted item was the only one on that date, deselect
      const remaining = allItems.filter(i => i.date === item.date && i.id !== item.id);
      if (remaining.length === 0) setSelectedDate(null);
      log({ action: "calendar_entry_deleted", module: "compliance_calendar", entityName: item.label });
    } catch (e) {
      console.error('[Calendar] Delete failed:', e.message);
      alert('Delete failed: ' + e.message);
    }
  }

  function handleExport() {
    const rows = allItems.filter(d => d.date >= tStr).sort((a, b) => a.date.localeCompare(b.date));
    downloadCSV(rows, `compliance-calendar-${tStr}.csv`);
    log({ action: "calendar_export", module: "compliance_calendar", meta: { count: rows.length } });
  }

  // ─── Styles ──────────────────────────────────────────────────────────────────
  const card = { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 20 };
  const btnBase = { border: "none", borderRadius: 7, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontWeight: 700, fontSize: 12 };
  const inputStyle = { background: C.bg, border: `1px solid ${C.border}`, borderRadius: 7, color: C.text, padding: "8px 12px", fontSize: 13, fontFamily: "'DM Mono', monospace", outline: "none", width: "100%", boxSizing: "border-box" };

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: "24px 28px", fontFamily: "'DM Mono', monospace", color: C.text, background: C.bg, minHeight: "100vh" }}>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontFamily: "'Syne', sans-serif", fontWeight: 700 }}>{t('calendar.title')}</h1>
          <p style={{ margin: "4px 0 0", color: C.textMid, fontSize: 12 }}>{t('calendar.subtitle')}</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button onClick={() => openAdd()} style={{ ...btnBase, background: C.accent, color: "#000", padding: "9px 18px" }}>+ {t('calendar.add')}</button>
          <button onClick={goToday} style={{ ...btnBase, background: "transparent", border: `1px solid ${C.border}`, color: C.text, padding: "8px 16px" }}>{t('calendar.today')}</button>
          <button onClick={handleExport} style={{ ...btnBase, background: "transparent", border: `1px solid ${C.border}`, color: C.textMid, padding: "8px 16px" }}>{t('calendar.export')}</button>
        </div>
      </div>

      {/* Legend */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        {Object.entries(TYPE_META).map(([k, v]) => (
          <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textMid }}>
            <div style={{ width: 10, height: 10, borderRadius: 3, background: v.color }} />{v.label}
          </div>
        ))}
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textMid, marginLeft: 6 }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: `${C.danger}40` }} /> {t('calendar.overdue')}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.textMid }}>
          <div style={{ width: 10, height: 10, borderRadius: 3, background: `${C.warn}40` }} /> {t('calendar.dueSoon')}
        </div>
      </div>

      {error && (
        <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, borderRadius: 8, padding: "10px 16px", color: C.danger, fontSize: 12, marginBottom: 16 }}>{error}</div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 20 }}>

        {/* ── Calendar ── */}
        <div style={card}>
          {/* Month nav */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
            <button onClick={prevMonth} style={{ ...btnBase, background: "rgba(255,255,255,0.06)", border: `1px solid ${C.border}`, color: C.text, padding: "5px 14px", fontSize: 16, fontWeight: 400 }}>‹</button>
            <span style={{ fontFamily: "'Syne', sans-serif", fontWeight: 700, fontSize: 16 }}>{MONTH_NAMES[viewMonth]} {viewYear}</span>
            <button onClick={nextMonth} style={{ ...btnBase, background: "rgba(255,255,255,0.06)", border: `1px solid ${C.border}`, color: C.text, padding: "5px 14px", fontSize: 16, fontWeight: 400 }}>›</button>
          </div>

          {/* DOW headers */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
            {DOW.map(d => (
              <div key={d} style={{ textAlign: "center", color: C.textDim, fontSize: 10, letterSpacing: "0.08em", padding: "4px 0" }}>{d}</div>
            ))}
          </div>

          {/* Grid cells */}
          {loading ? (
            <div style={{ textAlign: "center", color: C.textMid, padding: 40 }}>{t('common.loading')}</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2 }}>
              {Array.from({ length: firstDow }).map((_, i) => <div key={`e-${i}`} style={{ minHeight: 80 }} />)}
              {days.map(dateStr => {
                const items = deadlineMap[dateStr] || [];
                const isToday    = dateStr === tStr;
                const isSelected = dateStr === selectedDate;
                const bg = items.length > 0 ? urgencyBg(dateStr) : "transparent";
                // Debug: Log dates with items
                if (items.length > 0) {
                  console.log(`[Calendar] Date ${dateStr} has ${items.length} items:`, items.map(i => i.label));
                }
                return (
                  <div
                    key={dateStr}
                    onClick={() => setSelectedDate(isSelected ? null : dateStr)}
                    style={{
                      minHeight: 80,
                      background: isSelected ? `${C.accent}18` : bg,
                      border: isToday ? `2px solid ${C.accent}` : isSelected ? `1px solid ${C.accent}80` : `1px solid ${C.border}40`,
                      borderRadius: 6,
                      padding: 4,
                      cursor: "pointer",
                      transition: "background 0.15s",
                      position: "relative",
                    }}
                  >
                    <div style={{ fontSize: 11, color: isToday ? C.accent : C.textMid, fontWeight: isToday ? 700 : 400, marginBottom: 3 }}>
                      {parseInt(dateStr.slice(8), 10)}
                      {isToday && <span style={{ fontSize: 8, marginLeft: 2, color: C.accent }}>●</span>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {items.slice(0, 3).map(item => (
                        <div key={item.id} style={{ background: TYPE_META[item.type]?.color || C.accent, color: "#000", borderRadius: 3, fontSize: 9, padding: "2px 4px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 700, letterSpacing: "0.03em", opacity: item.system ? 1 : 0.85 }} title={item.label}>
                          {item.type}{!item.system ? " *" : ""}
                        </div>
                      ))}
                      {items.length > 3 && (
                        <div style={{ fontSize: 9, color: C.textMid, paddingLeft: 2 }}>+{items.length - 3} {t('calendar.more')}</div>
                      )}
                    </div>
                    {/* Quick-add on hover via double-click */}
                  </div>
                );
              })}
            </div>
          )}

          {/* Selected date detail */}
          {selectedDate && (
            <div style={{ marginTop: 16, background: `${C.accent}0A`, border: `1px solid ${C.accent}40`, borderRadius: 8, padding: 14 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div style={{ fontSize: 11, color: C.accent, fontWeight: 700, letterSpacing: "0.08em" }}>
                  {new Date(selectedDate + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
                </div>
                <button
                  onClick={() => openAdd(selectedDate)}
                  style={{ ...btnBase, background: C.accent, color: "#000", padding: "4px 12px", fontSize: 11 }}
                >
                  {t('calendar.addToDay')}
                </button>
              </div>

              {(deadlineMap[selectedDate] || []).length === 0 ? (
                <div style={{ color: C.textDim, fontSize: 12, padding: "8px 0" }}>{t('calendar.noEventsMsg')}</div>
              ) : (
                (deadlineMap[selectedDate] || []).map(item => (
                  <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", borderBottom: `1px solid ${C.border}40` }}>
                    <div style={{ width: 8, height: 8, borderRadius: 2, background: TYPE_META[item.type]?.color || C.accent, flexShrink: 0 }} />
                    <span style={{ fontSize: 12, color: C.text, flex: 1 }}>{item.label}</span>
                    <span style={{ fontSize: 10, color: C.textDim, marginRight: 6 }}>{item.status}</span>
                    {/* Edit/Delete — only for manual entries */}
                    {!item.system && (
                      <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                        <button
                          onClick={() => openEdit(item)}
                          style={{ ...btnBase, background: "transparent", border: `1px solid ${C.border}`, color: C.textMid, padding: "3px 9px", fontSize: 10, fontWeight: 600 }}
                        >
                          {t('common.edit')}
                        </button>
                        <button
                          onClick={() => handleDelete(item)}
                          style={{ ...btnBase, background: "transparent", border: `1px solid ${C.danger}60`, color: C.danger, padding: "3px 9px", fontSize: 10, fontWeight: 600 }}
                        >
                          {t('common.delete')}
                        </button>
                      </div>
                    )}
                    {item.system && (
                      <span style={{ fontSize: 9, color: C.textDim, fontStyle: "italic" }}>auto</span>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* ── Sidebar ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {overdue.length > 0 && (
            <div style={{ ...card, border: `1px solid ${C.danger}60` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.danger, letterSpacing: "0.1em", marginBottom: 12 }}>{t('calendar.overdueSectionTitle', { count: overdue.length })}</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto" }}>
                {overdue.map(item => (
                  <SidebarItem key={item.id} item={item} C={C} onEdit={!item.system ? openEdit : null} onDelete={!item.system ? handleDelete : null} t={t} TYPE_META={TYPE_META} />
                ))}
              </div>
            </div>
          )}

          <div style={card}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textMid, letterSpacing: "0.1em" }}>{t('calendar.upcomingSectionTitle', { count: upcoming.length })}</div>
            </div>
            {/* View duration tabs */}
            <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
              {[30, 60, 90, 120].map(days => (
                <button
                  key={days}
                  onClick={() => setViewDays(days)}
                  style={{
                    background: viewDays === days ? C.accent : "transparent",
                    border: `1px solid ${viewDays === days ? C.accent : C.border}`,
                    color: viewDays === days ? C.bg : C.textMid,
                    borderRadius: 5,
                    padding: "4px 10px",
                    fontSize: 11,
                    cursor: "pointer",
                    fontFamily: "'DM Mono', monospace",
                    fontWeight: viewDays === days ? 600 : 400,
                    transition: "all 0.15s",
                  }}
                >
                  {days}d
                </button>
              ))}
            </div>
            {loading ? (
              <div style={{ color: C.textDim, fontSize: 12 }}>{t('common.loading')}</div>
            ) : upcoming.length === 0 ? (
              <div style={{ color: C.textDim, fontSize: 12, textAlign: "center", padding: 20 }}>{t('calendar.noDeadlines')}</div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 480, overflowY: "auto" }}>
                {upcoming.map(item => (
                  <SidebarItem key={item.id} item={item} C={C} onEdit={!item.system ? openEdit : null} onDelete={!item.system ? handleDelete : null} t={t} TYPE_META={TYPE_META} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Add / Edit Modal ── */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 28, width: 460, maxWidth: "90vw" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <span style={{ color: C.text, fontWeight: 700, fontSize: 16 }}>{editingEntry ? t('calendar.editEntry') : t('calendar.addCalendarEntry')}</span>
              <button onClick={() => { setShowModal(false); setEditingEntry(null); }} style={{ background: "none", border: "none", color: C.textDim, cursor: "pointer", fontSize: 20 }}>×</button>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <div>
                <label style={{ color: C.textDim, fontSize: 11, letterSpacing: "0.08em", display: "block", marginBottom: 5 }}>{t('calendar.dateLabel')}</label>
                <input type="date" value={draft.date} onChange={e => setDraft(d => ({ ...d, date: e.target.value }))} style={inputStyle} />
              </div>
              <div>
                <label style={{ color: C.textDim, fontSize: 11, letterSpacing: "0.08em", display: "block", marginBottom: 5 }}>{t('calendar.typeLabel')}</label>
                <select value={draft.type} onChange={e => setDraft(d => ({ ...d, type: e.target.value }))} style={inputStyle}>
                  {MANUAL_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label style={{ color: C.textDim, fontSize: 11, letterSpacing: "0.08em", display: "block", marginBottom: 5 }}>{t('calendar.titleLabel')}</label>
                <input value={draft.label} onChange={e => setDraft(d => ({ ...d, label: e.target.value }))} placeholder={t('calendar.datePlaceholder')} style={inputStyle} />
              </div>
              <div>
                <label style={{ color: C.textDim, fontSize: 11, letterSpacing: "0.08em", display: "block", marginBottom: 5 }}>{t('calendar.statusLabel')}</label>
                <select value={draft.status} onChange={e => setDraft(d => ({ ...d, status: e.target.value }))} style={inputStyle}>
                  {["pending", "in_progress", "completed", "overdue"].map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1).replace("_", " ")}</option>)}
                </select>
              </div>
              <div>
                <label style={{ color: C.textDim, fontSize: 11, letterSpacing: "0.08em", display: "block", marginBottom: 5 }}>{t('calendar.notesLabel')}</label>
                <textarea value={draft.notes} onChange={e => setDraft(d => ({ ...d, notes: e.target.value }))} placeholder={t('calendar.notesPlaceholder')} rows={3} style={{ ...inputStyle, resize: "vertical" }} />
              </div>
              <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                <button onClick={() => { setShowModal(false); setEditingEntry(null); }}
                  style={{ flex: 1, padding: "10px", background: "transparent", border: `1px solid ${C.border}`, borderRadius: 7, color: C.textMid, cursor: "pointer", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                  {t('calendar.cancelBtn')}
                </button>
                <button onClick={handleSave} disabled={saving || !draft.date || !draft.label.trim()}
                  style={{ flex: 1, padding: "10px", background: draft.date && draft.label.trim() ? C.accent : C.border, border: "none", borderRadius: 7, color: C.bg, fontWeight: 700, cursor: draft.date && draft.label.trim() ? "pointer" : "default", fontFamily: "'DM Mono', monospace", fontSize: 13 }}>
                  {saving ? t('calendar.savingBtn') : editingEntry ? t('calendar.saveChangesBtn') : t('calendar.addEntryBtn')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Sidebar Item ─────────────────────────────────────────────────────────────
function SidebarItem({ item, C, onEdit, onDelete, t, TYPE_META }) {
  const diff = daysFromToday(item.date);
  const meta = TYPE_META?.[item.type] || { color: C.accent };
  const urgColor = diff < 0 ? C.danger : diff <= 3 ? C.warn : C.textMid;
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "7px 8px", borderRadius: 6, background: diff < 0 ? `${C.danger}10` : diff <= 3 ? `${C.warn}10` : "transparent" }}>
      <div style={{ width: 6, height: 6, borderRadius: 2, background: meta.color, marginTop: 4, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.label}</div>
        <div style={{ fontSize: 10, color: urgColor, marginTop: 2 }}>
          {diff < 0 ? t('calendar.daysOverdue', { count: Math.abs(diff) }) : diff === 0 ? t('calendar.dueToday') : t('calendar.daysRemaining', { count: diff })}
          {" · "}
          {(() => {
            try {
              const dateStr = item.date && typeof item.date === 'string' ? item.date : '';
              if (!dateStr) return 'No date';
              const d = new Date(dateStr + "T12:00:00");
              return isNaN(d.getTime()) ? 'Invalid Date' : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
            } catch {
              return 'Invalid Date';
            }
          })()}
        </div>
      </div>
      {(onEdit || onDelete) && (
        <div style={{ display: "flex", gap: 3, flexShrink: 0, marginTop: 2 }}>
          {onEdit && (
            <button onClick={() => onEdit(item)} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 4, color: C.textDim, cursor: "pointer", fontSize: 10, padding: "2px 7px", fontFamily: "'DM Mono', monospace" }}>{t('calendar.editBtn')}</button>
          )}
          {onDelete && (
            <button onClick={() => onDelete(item)} style={{ background: "none", border: `1px solid ${C.danger}50`, borderRadius: 4, color: C.danger, cursor: "pointer", fontSize: 10, padding: "2px 7px", fontFamily: "'DM Mono', monospace" }}>{t('calendar.deleteBtn')}</button>
          )}
        </div>
      )}
    </div>
  );
}
